import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";

import { env } from "../../utils/env";
import * as persistentStorage from "../../utils/persistentStorage";

// --- Main Interfaces ---
interface UnifiedUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
}

interface UnifiedOrganization {
  id: string;
  name: string;
  slug: string | null;
  imageUrl: string | null;
}

interface AuthState {
  user: UnifiedUser | null;
  organization: UnifiedOrganization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  jwt: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
}

interface UnifiedAuthContextType
  extends Omit<AuthState, "refreshToken" | "expiresAt"> {
  authenticate: (payload: {
    code?: string;
    clerk_token?: string;
  }) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

// --- Zod Schemas for API Validation ---
const AuthApiResponseSchema = z.object({
  jwt: z.string(),
  refreshToken: z.string(),
  expiresAt: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }),
  organization: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }),
});

const RefreshTokenResponseSchema = z.object({
  jwt: z.string(),
  refreshToken: z.string(),
  expiresAt: z.string(),
});

// --- Context Definition ---
const UnifiedAuthContext = createContext<UnifiedAuthContextType | null>(null);

// --- Provider Component ---
interface UnifiedAuthProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = "frontier_unified_auth_state";

const getApiBaseUrl = () => {
  const apiUrl = env.API_URL;
  if (!apiUrl) {
    throw new Error("API_URL is required for authentication");
  }
  return apiUrl;
};

export function UnifiedAuthProvider({
  children,
}: Readonly<UnifiedAuthProviderProps>) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    organization: null,
    isAuthenticated: false,
    isLoading: true,
    jwt: null,
    refreshToken: null,
    expiresAt: null,
  });

  const refreshAndSetAuthState = useCallback(
    async (refreshTokenValue: string) => {
      const response = await fetch(
        `${getApiBaseUrl()}/api/auth/refresh-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        },
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      const validatedData = RefreshTokenResponseSchema.parse(data);

      setAuthState((prevState) => {
        const newState = {
          ...prevState,
          isAuthenticated: true,
          isLoading: false,
          jwt: validatedData.jwt,
          refreshToken: validatedData.refreshToken,
          expiresAt: new Date(validatedData.expiresAt).getTime(),
        };
        persistentStorage
          .setItem(STORAGE_KEY, JSON.stringify(newState))
          .catch((error) => console.error("Failed to save auth state:", error));
        return newState;
      });
    },
    [],
  );

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedState = await persistentStorage.getItem(STORAGE_KEY);
        if (storedState) {
          const parsedState = JSON.parse(storedState) as AuthState;
          if (parsedState.expiresAt && parsedState.expiresAt > Date.now()) {
            setAuthState({ ...parsedState, isLoading: false });
          } else if (parsedState.refreshToken) {
            await refreshAndSetAuthState(parsedState.refreshToken);
          } else {
            throw new Error("Session expired, no refresh token found");
          }
        } else {
          // No stored state - fresh app start
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            isAuthenticated: false,
          }));
        }
      } catch (error) {
        console.error(
          "Failed to initialize auth state, clearing state.",
          error,
        );
        persistentStorage
          .removeItem(STORAGE_KEY)
          .catch((removeError) =>
            console.error("Failed to clear auth state:", removeError),
          );
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
        }));
      }
    };

    initializeAuth();
  }, [refreshAndSetAuthState]);

  const clearAuthState = useCallback(() => {
    persistentStorage
      .removeItem(STORAGE_KEY)
      .catch((error) => console.error("Failed to clear auth state:", error));
    setAuthState({
      user: null,
      organization: null,
      isAuthenticated: false,
      isLoading: false,
      jwt: null,
      refreshToken: null,
      expiresAt: null,
    });
  }, []);

  const authenticate = useCallback(
    async (payload: { code?: string; clerk_token?: string }) => {
      // Use the same endpoint for both desktop codes and Clerk tokens
      const endpoint = "/api/auth/authenticate";

      const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ?? errorData.message ?? "Authentication failed",
        );
      }

      const rawData = await response.text();

      let data: unknown;
      try {
        data = JSON.parse(rawData);
      } catch (parseError) {
        throw new Error("Invalid response format from authentication service");
      }

      const validatedData = AuthApiResponseSchema.parse(data);

      const newAuthState: AuthState = {
        user: validatedData.user,
        organization: validatedData.organization,
        isAuthenticated: true,
        isLoading: false,
        jwt: validatedData.jwt,
        refreshToken: validatedData.refreshToken,
        expiresAt: new Date(validatedData.expiresAt).getTime(),
      };

      setAuthState(newAuthState);
      persistentStorage
        .setItem(STORAGE_KEY, JSON.stringify(newAuthState))
        .catch((error) => console.error("Failed to save auth state:", error));
    },
    [],
  );

  const refreshToken = useCallback(async () => {
    if (!authState.refreshToken) {
      throw new Error("No refresh token available");
    }
    await refreshAndSetAuthState(authState.refreshToken);
  }, [authState.refreshToken, refreshAndSetAuthState]);

  const contextValue = useMemo(
    () => ({
      ...authState,
      authenticate,
      logout: clearAuthState,
      refreshToken,
    }),
    [authState, authenticate, clearAuthState, refreshToken],
  );

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

// --- Hooks for consuming context ---
export function useAuth(): UnifiedAuthContextType {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a UnifiedAuthProvider");
  }
  return context;
}

export function useUser() {
  const { user, isLoading } = useAuth();
  return { user, isLoaded: !isLoading };
}

export function useOrganization() {
  const { organization, isLoading } = useAuth();
  return { organization, isLoaded: !isLoading };
}
